import type { Message, ChatContext } from '../../../types/models';
import type {
  GroqConfig,
  GroqCompletionRequest,
  GroqCompletionResponse,
  GroqErrorResponse
} from '../../../types/providerTypes';
import { formatMessages } from './messageFormatter';
import { APIError } from '../../error/errorHandler';
import { getProviderConfig, getProviderUrl, getProviderEndpoint } from '../../../config/modelsDatabase';
import { usageService } from '../../../services/UsageService';
import { useStore } from '../../../store/useStore';

// Helper function to check environment without exposing keys
const getSystemApiKey = (): string | null => {
  // Check if the environment variable exists without directly accessing its value
  const hasKey = Object.keys(import.meta.env).includes('SYSTEM_GROQ_API_KEY');
  
  // For a secure implementation, this should be replaced with a server proxy call
  return hasKey ? import.meta.env.SYSTEM_GROQ_API_KEY as string : null;
};

export async function enhancePrompt(
  prompt: string,
  apiKey: string,
  useSystemApi: boolean = false
): Promise<string> {
  try {
    // Sanitize input to prevent injection attacks
    const sanitizedPrompt = prompt.slice(0, 1000).replace(/"/g, '\\"');
    
    // Get Groq configuration
    const config = getProviderConfig('groq');
    if (!config) {
      throw new Error('Groq provider configuration not found');
    }

    const chatEndpoint = getProviderEndpoint('groq', 'chat');
    if (!chatEndpoint) {
      throw new Error('Groq chat endpoint not found');
    }
    
    // Security: Handle system API token approach
    let effectiveApiKey = apiKey;
    if (useSystemApi || apiKey === 'USE_SYSTEM_API_PROXY') {
      // IMPORTANT: In production, this should be replaced with a proxy server call
      // rather than directly using environment variables
      const systemKey = getSystemApiKey();
      if (!systemKey) {
        throw new Error('System API not configured');
      }
      effectiveApiKey = systemKey;
    }

    const requestData: GroqCompletionRequest = {
      model: 'llama-3.3-70b-versatile',
      messages: [{
        role: 'user',
        content: `Please enhance the following prompt to be more detailed, specific, and effective: "${sanitizedPrompt}"`
      }],
      temperature: 0.6,
      max_completion_tokens: 1024,
      top_p: 0.95
    };

    const response = await fetch(getProviderUrl('groq', chatEndpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${effectiveApiKey}`,
        ...config.headers
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      if (response.status === 503) {
        throw new Error('Groq service is temporarily unavailable. Please try again later.');
      }
      const errorData = await response.json() as GroqErrorResponse;
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json() as GroqCompletionResponse;
    
    if (!data.choices?.[0]?.message?.content) {
      throw new Error('Invalid response format from Groq');
    }

    // Sanitize response to prevent regex issues - particularly for Llama models
    // Remove potentially problematic regex patterns like named capture groups
    let responseContent = data.choices[0].message.content;
    if (modelId.toLowerCase().includes('llama')) {
      try {
        // Remove named capture groups that might cause regex issues
        responseContent = responseContent.replace(/\(\?[<P][^)]+\)/g, '(...)');
      } catch (sanitizeError) {
        console.warn('Error sanitizing response content:', sanitizeError);
      }
    }

    return responseContent;
  } catch (error) {
    console.error('Groq API Error:', error instanceof Error ? error.name : 'Unknown error');
    throw new APIError(
      error instanceof Error 
        ? error.message.replace(/key|api|token|secret/gi, '[REDACTED]')
        : 'Failed to communicate with Groq',
      'Groq'
    );
  }
}

export async function sendMessage(
  message: string | any[],
  apiKey: string,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[]
): Promise<string> {
  try {
    // Get Groq configuration
    const config = getProviderConfig('groq');
    if (!config) {
      throw new Error('Groq provider configuration not found');
    }

    const chatEndpoint = getProviderEndpoint('groq', 'chat');
    if (!chatEndpoint) {
      throw new Error('Groq chat endpoint not found');
    }
    
    // Security: Handle system API token approach
    let effectiveApiKey = apiKey;
    if (apiKey === 'USE_SERVER_PROXY' || apiKey === 'USE_SYSTEM_API_PROXY') {
      // IMPORTANT: In production, this should be replaced with a proxy server call
      // rather than directly using environment variables
      const systemKey = getSystemApiKey();
      if (!systemKey) {
        throw new Error('System API not configured');
      }
      effectiveApiKey = systemKey;
    }

    // Sanitize and validate input
    let sanitizedMessage: string | any[] = message;
    if (typeof message === 'string') {
      // For string messages, basic sanitization
      sanitizedMessage = message.slice(0, 32000); // Reasonable limit
    } else if (Array.isArray(message)) {
      // For array messages (multimodal content), validate each element
      sanitizedMessage = message.map(item => {
        if (typeof item === 'object' && item.type === 'text' && typeof item.text === 'string') {
          return {
            ...item,
            text: item.text.slice(0, 32000) // Limit text length
          };
        }
        return item;
      });
    }

    const formattedMessages = formatMessages(sanitizedMessage, context, previousMessages);
    
    // Calculate total tokens for DeepSeek model context window limit
    if (modelId.toLowerCase() === 'deepseek-r1-distill-llama-70b') {
      const totalTokens = formattedMessages.reduce((sum, msg) => {
        // Estimate tokens based on content type
        const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
        return sum + content.length / 4; // Rough estimation
      }, 0);
      
      if (totalTokens > 128000) {
        throw new Error('Input exceeds DeepSeek-R1-Distill-Llama-70B context window limit of 128k tokens');
      }
    }

    const requestData: GroqCompletionRequest = {
      model: modelId.toLowerCase(),
      messages: formattedMessages,
      temperature: 0.6,
      max_completion_tokens: modelId.toLowerCase() === 'deepseek-r1-distill-llama-70b' ? 4096 : 1024,
      top_p: 0.95,
      reasoning_format: modelId.toLowerCase() === 'deepseek-r1-distill-llama-70b' ? 'raw' : undefined
    };

    const response = await fetch(getProviderUrl('groq', chatEndpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${effectiveApiKey}`,
        ...config.headers
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      if (response.status === 503) {
        throw new Error('Groq service is temporarily unavailable. Please try again later.');
      }
      const errorData = await response.json() as GroqErrorResponse;
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json() as GroqCompletionResponse;
    
    if (!data.choices?.[0]?.message?.content) {
      throw new Error('Invalid response format from Groq');
    }

    // Record usage if available in the response
    // Don't log full API response which might contain sensitive data
    console.log('Groq API Response received', {
      status: 'success',
      hasUsageData: !!data.usage
    });
    
    if (data.usage) {
      // Log only the necessary usage metrics without sensitive data
      console.log('Recording Groq usage metrics', {
        model: modelId,
        totalTokens: data.usage.total_tokens
      });
      
      // Get current user and topUpValue from store
      const { user, topUpValue } = useStore.getState();
      if (!user) {
        throw new Error('User not authenticated');
      }

      await usageService.recordUsage(user.uid, modelId, {
        prompt_tokens: data.usage.prompt_tokens,
        completion_tokens: data.usage.completion_tokens,
        total_tokens: data.usage.total_tokens
      }, topUpValue);
    } else {
      console.warn('No usage data available in response');
    }

    // Sanitize response to prevent regex issues - particularly for Llama models
    // Remove potentially problematic regex patterns like named capture groups
    let responseContent = data.choices[0].message.content;
    if (modelId.toLowerCase().includes('llama')) {
      try {
        // Remove named capture groups that might cause regex issues
        responseContent = responseContent.replace(/\(\?[<P][^)]+\)/g, '(...)');
      } catch (sanitizeError) {
        console.warn('Error sanitizing response content:', sanitizeError);
      }
    }

    return responseContent;
  } catch (error) {
    console.error('Groq API Error:', error instanceof Error ? error.name : 'Unknown error type');
    throw new APIError(
      error instanceof Error 
        ? error.message.replace(/key|api|token|secret/gi, '[REDACTED]')
        : 'Failed to communicate with Groq',
      'Groq'
    );
  }
}